// config/formConfig.ts

export type Section =
  | 'General Information'
  | 'Origin Charges'
  | 'Freight'
  | 'Destination Charges'
  | 'U.S. Inland Freight'
  | 'Totals'
  | 'Other charges & TC'
  | 'Attachments';

export type FieldType = 'text' | 'number' | 'textarea' | 'select' | 'file'|'date';

export interface Option {
  label: string;
  value: string;
  // disable?: boolean
}

export interface FieldDef {
  id: string;
  label: string;
  section: Section;
  type: FieldType;
  errMsg?: string;
  note?: string;
  required?: boolean;
  options?: Option[]; // for select
}

export type SheetKey =  'FOB' |'EXW' | 'DAP/DDP/DDU' | 'CIF/CFR' | 'AIR';

export const INCOTERM_OPTIONS = ['FOB','EXW',  'CIF/CFR', 'DAP/DDP/DDU', 'AIR'] as const;

export const FREIGHT_TYPE_OPTIONS = [
  { label: 'FCL', value: 'FCL' },
  { label: 'LCL', value: 'LCL' },
  { label: 'Air', value: 'Air' },
] as const;

export function sheetFromIncoterm(incoterm: (typeof INCOTERM_OPTIONS)[number]): SheetKey {
  switch (incoterm) {
        case 'FOB':
      return 'FOB';
    case 'EXW':
      return 'EXW';
    case 'CIF/CFR':
      return 'CIF/CFR';
    case 'DAP/DDP/DDU':
      return 'DAP/DDP/DDU';
    case 'AIR':
      return 'AIR';
    default:
      return 'FOB';
  }
}

// Common fields reused across sheets
const commonGeneral: FieldDef[] = [
  {
    id: 'general.freightType',
    label: 'Freight Type (FCL/LCL/Air)',
    section: 'General Information',
    type: 'select',
    required: true,
    errMsg: 'Please select a Freight Type',
    options: [
      { label: 'FCL', value: 'FCL' },
      { label: 'LCL', value: 'LCL' },
      { label: 'Air', value: 'Air' },
    ],
    note: 'Select from dropdown',
  },
  {
    id: 'general.rateValidity',
    label: 'Rate Validity',
    section: 'General Information',
    type: 'text',
    required: true,
    errMsg: 'Rate Validity is required',
  },
  {
    id: 'general.freeDays',
    label: 'Free Days (at destination)',
    section: 'General Information',
    type: 'number',
    required: true,
    errMsg: 'Free Days are required',
    note: 'Enter a numeric value (days)',
  },
];

const freightBlock: FieldDef[] = [
  {
    id: 'freight.mainFreight',
    label: 'Main Freight (Ocean/Air)',
    section: 'Freight',
    type: 'select',
    options: [
      { label: 'OCEAN', value: 'OCEAN' },
      { label: 'AIR', value: 'AIR' },
    ],
    required: true,
    errMsg: 'Main Freight is required',
    note: 'Mandatory for EXW/FOB/CIF/CFR/DAP/DDP/DDU',
  },
  {
    id: 'freight.carrier',
    label: 'Carrier',
    section: 'Freight',
    type: 'text',
    // optional
  },
  {
    id: 'freight.transitTime',
    label: 'Transit Time (Days)',
    section: 'Freight',
    type: 'number',
    // optional
    note: 'Numeric days; excluded from auto-sum',
  },
];

const originChargesBlock: FieldDef[] = [
  {
    id: 'origin.othc',
    label: 'OTHC (Origin THC)',
    section: 'Origin Charges',
    type: 'number',
    required: true,
    errMsg: 'OTHC is required',
    note: 'Mandatory for FOB/CIF/DAP/DDP/EXW',
  },
  {
    id: 'origin.doFeeOrigin',
    label: 'DO Fee – Origin',
    section: 'Origin Charges',
    type: 'number',
    required: true,
    errMsg: 'DO Fee – Origin is required',
    note: 'Mandatory for FOB/CIF/DAP/DDP/EXW',
  },
  {
    id: 'origin.customsOrigin',
    label: 'Customs Clearance – Origin',
    section: 'Origin Charges',
    type: 'number',
    required: true,
    errMsg: 'Customs Clearance – Origin is required',
    note: 'Mandatory for FOB/CIF/DAP/DDP/EXW',
  },
  { id: 'origin.blCharges', label: 'BL / Switch BL Charges', section: 'Origin Charges', type: 'number' },
  { id: 'origin.exportDocFee', label: 'Export Documentation Fee', section: 'Origin Charges', type: 'number' },
  { id: 'origin.sealFee', label: 'Seal Fee', section: 'Origin Charges', type: 'number' },
  { id: 'origin.otherOrigin', label: 'Other Origin Charges', section: 'Origin Charges', type: 'number' },
];

const destinationChargesBlock: FieldDef[] = [
  {
    id: 'dest.dthc',
    label: 'DTHC (Destination THC)',
    section: 'Destination Charges',
    type: 'number',
    required: true,
    errMsg: 'DTHC is required',
    note: 'Mandatory for FOB/CIF/CFR/DAP/DDP/DDU',
  },
  {
    id: 'dest.doFeeDest',
    label: 'DO Fee – Destination',
    section: 'Destination Charges',
    type: 'number',
    required: true,
    errMsg: 'DO Fee – Destination is required',
    note: 'Mandatory for FOB/CIF/CFR/DAP/DDP/DDU',
  },
  {
    id: 'dest.customsDest',
    label: 'Customs Clearance – Destination',
    section: 'Destination Charges',
    type: 'number',
    required: true,
    errMsg: 'Customs Clearance – Destination is required',
    note: 'Mandatory for FOB/CIF/CFR/DAP/DDP/DDU',
  },
  { id: 'dest.portHandling', label: 'Port Handling / Wharfage', section: 'Destination Charges', type: 'number' },
  { id: 'dest.inlandDelivery', label: 'Inland Delivery (if door)', section: 'Destination Charges', type: 'number' },
  { id: 'dest.otherDest', label: 'Other Destination Charges', section: 'Destination Charges', type: 'number' },
];

const usInlandBlock: FieldDef[] = [
  {
    id: 'us.prePull',
    label: 'Pre-pull',
    section: 'U.S. Inland Freight',
    type: 'number',
    note: 'Only for pick up from US origin order',
  },
  {
    id: 'us.chassisSplit',
    label: 'Chassis Split',
    section: 'U.S. Inland Freight',
    type: 'number',
    note: 'Only for pick up from US origin order',
  },
  {
    id: 'us.chassisFee',
    label: 'Chassis Fee',
    section: 'U.S. Inland Freight',
    type: 'number',
    note: 'Only for pick up from US origin order',
  },
  {
    id: 'us.otherInland',
    label: 'Other Inland Charges',
    section: 'U.S. Inland Freight',
    type: 'number',
    note: 'Only for pick up from US origin order',
  },
];

const totalsBlock: FieldDef[] = [
  {
    id: 'totals.allIn',
    label: 'Total All-In Cost',
    section: 'Totals',
    type: 'number',
    required: true,
    errMsg: 'Total All-In Cost is required',
    note: 'Sum of all applicable costs',
  },
];

const otherTcBlock: FieldDef[] = [
  {
    id: 'other.tc',
    label: 'Other charges & TC',
    section: 'Other charges & TC',
    type: 'textarea',
    note: 'Special surcharges, storage conditions, CAF %, rate validity, etc.',
  },
];

const attachmentsBlock: FieldDef[] = [
  {
    id: 'attachments.vendorPdf',
    label: 'Vendor’s detailed PDF quote',
    section: 'Attachments',
    type: 'file',
    note: 'Optional but recommended',
  },
];

// Quote Template (generic) — keep if needed
const quoteTemplateFields: FieldDef[] = [
  {
    id: 'general.incoterm',
    label: 'Incoterm (FOB/CIF/CFR/DAP/DDP/DDU/EXW)',
    section: 'General Information',
    type: 'select',
    required: true,
    errMsg: 'Please select an Incoterm',
    options: [
      { label: 'EXW', value: 'EXW' },
      { label: 'FOB', value: 'FOB' },
      { label: 'CIF/CFR', value: 'CIF/CFR' },
      { label: 'DAP/DDP/DDU', value: 'DAP/DDP/DDU' },
      { label: 'AIR', value: 'AIR' },
    ],
    note: 'Select from dropdown',
  },
  {
    id: 'general.freightType',
    label: 'Freight Type (FCL/LCL/Air)',
    section: 'General Information',
    type: 'select',
    required: true,
    errMsg: 'Please select a Freight Type',
    options: [
      { label: 'FCL', value: 'FCL' },
      { label: 'LCL', value: 'LCL' },
      { label: 'Air', value: 'Air' },
    ],
    note: 'Select from dropdown',
  },
  {
    id: 'general.rateValidity',
    label: 'Rate Validity',
    section: 'General Information',
    type: 'text',
    required: true,
    errMsg: 'Rate Validity is required',
  },
  {
    id: 'general.freeDays',
    label: 'Free Days (at destination)',
    section: 'General Information',
    type: 'number',
    required: true,
    errMsg: 'Free Days are required',
  },
  {
    id: 'general.volume',
    label: 'Volume (CBM / Chargeable Wt)',
    section: 'General Information',
    type: 'text',
    note: 'Mandatory for AIR FREIGHT',
  },
  ...originChargesBlock,
  ...freightBlock,
  ...destinationChargesBlock,
  ...usInlandBlock,
  ...totalsBlock,
  ...otherTcBlock,
  ...attachmentsBlock,
];

// EXW sheet
const exwFields: FieldDef[] = [
  commonGeneral[0],
  ...freightBlock,
  ...originChargesBlock,
  {
    id: 'freight.carrierEXW',
    label: 'Carrier',
    section: 'Freight',
    type: 'text',
  },
  {
    id: 'freight.transitTimeEXW',
    label: 'Transit Time (Days)',
    section: 'Freight',
    type: 'number',
    note: 'Numeric days; excluded from auto-sum',
  },
  commonGeneral[1],
  commonGeneral[2],
  ...totalsBlock,
  ...otherTcBlock,
  ...attachmentsBlock,
];

// FOB sheet
const fobFields: FieldDef[] = [
  commonGeneral[0],
  ...freightBlock,
  ...destinationChargesBlock,
  commonGeneral[1],
  commonGeneral[2],
  ...totalsBlock,
  ...otherTcBlock,
  ...attachmentsBlock,
];

// DAP/DDP/DDU sheet
const dapddpddusFields: FieldDef[] = [
  commonGeneral[0],
  ...freightBlock,
  ...originChargesBlock,
  {
    id: 'freight.carrierDAP',
    label: 'Carrier',
    section: 'Freight',
    type: 'text',
  },
  {
    id: 'freight.transitTimeDAP',
    label: 'Transit Time (Days)',
    section: 'Freight',
    type: 'number',
    note: 'Numeric days; excluded from auto-sum',
  },
  ...destinationChargesBlock,
  commonGeneral[1],
  commonGeneral[2],
  ...totalsBlock,
  ...otherTcBlock,
  ...attachmentsBlock,
];

// CIF/CFR sheet
const cifcfrFields: FieldDef[] = [
  commonGeneral[0],
  ...freightBlock,
  ...originChargesBlock,
  ...destinationChargesBlock,
  commonGeneral[1],
  commonGeneral[2],
  ...totalsBlock,
  ...otherTcBlock,
  ...attachmentsBlock,
];

// AIR sheet
const airFields: FieldDef[] = [
  commonGeneral[0],
  ...freightBlock,
  ...originChargesBlock,
  {
    id: 'general.volume',
    label: 'Volume (CBM / Chargeable Wt)',
    section: 'General Information',
    type: 'text',
    required: true,
    errMsg: 'Volume is required for AIR freight',
    note: 'Mandatory for AIR FREIGHT',
  },
  ...destinationChargesBlock,
  commonGeneral[1],
  commonGeneral[2],
  ...totalsBlock,
  ...otherTcBlock,
  ...attachmentsBlock,
];

export const SHEET_FIELDS: Record<SheetKey, FieldDef[]> = {
  EXW: exwFields,
  FOB: fobFields,
  'DAP/DDP/DDU': dapddpddusFields,
  'CIF/CFR': cifcfrFields,
  AIR: airFields,
};
